"use client";

import { useState } from "react";
import { ArrowLeft, Check, Copy } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shadcn-components/ui/card";
import { Badge } from "@/shadcn-components/ui/badge";
import { Separator } from "@/shadcn-components/ui/separator";
import { useActiveAccount } from "@/lib/accounts/store";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information";
import {
  AccountMetricsRows,
  formatAccountMoney,
  useAccountMetrics,
} from "./AccountMetrics";

interface AccountDetailsScreenProps {
  onBack: () => void;
  embedded?: boolean;
}

const DetailRow = ({
  label,
  value,
  copy = false,
}: {
  label: string;
  value: string | number | null | undefined;
  /** When true, the value becomes click-to-copy with a hover affordance (credential values). */
  copy?: boolean;
}) => {
  const [copied, setCopied] = useState(false);
  const text = value == null || value === "" ? "" : String(value);
  const canCopy = copy && text.length > 0;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1000);
    } catch {
      /* clipboard blocked (insecure context) — value stays selectable */
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {canCopy ? (
        <button
          type="button"
          onClick={onCopy}
          title="Click to copy"
          className="group flex min-w-0 items-center justify-end gap-1.5 text-right font-mono text-foreground hover:text-blue-400"
        >
          <span className="min-w-0 truncate">{text}</span>
          {copied ? (
            <Check className="h-3.5 w-3.5 shrink-0 text-green-500" />
          ) : (
            <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          )}
        </button>
      ) : (
        <span className="min-w-0 truncate text-right font-mono text-foreground">
          {value ?? "--"}
        </span>
      )}
    </div>
  );
};

const formatArray = (value: number[] | null | undefined) =>
  value && value.length > 0 ? value.join(", ") : null;

export default function AccountDetailsScreen({
  onBack,
  embedded = false,
}: AccountDetailsScreenProps) {
  const activeAccount = useActiveAccount();
  const account = useAccountInformationStore((state) => state.account);
  const { profit } = useAccountMetrics();
  const currency = account?.currency || "USD";

  const content = (
    <>
      <div className="flex items-center justify-between border-b border-border bg-background px-4 py-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-9 w-9">
          <ArrowLeft className="h-5 w-5 text-blue-400" />
        </Button>
        <div className="min-w-0 flex-1 text-center">
          <h1 className="truncate text-lg font-medium text-foreground">
            Account Information
          </h1>
          <p className="truncate text-xs text-muted-foreground">
            {account?.login || activeAccount?.credentials.login || "No account"}
          </p>
        </div>
        <div className="h-9 w-9" />
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        <Card>
          <CardHeader className="px-4 pb-2 pt-4">
            <CardTitle className="text-sm">Money & Margin</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <AccountMetricsRows />
            <Separator className="my-3" />
            <DetailRow
              label="Floating P/L"
              value={formatAccountMoney(profit, currency)}
            />
            <DetailRow
              label="Blocked"
              value={formatAccountMoney(account?.blocked, currency)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="px-4 pb-2 pt-4">
            <CardTitle className="text-sm">Trading Settings</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <DetailRow label="Leverage" value={account?.leverage ? `1:${account.leverage}` : null} />
            <DetailRow label="Currency" value={currency} />
            <DetailRow label="Account Method" value={account?.accountMethod} />
            <DetailRow label="Margin Mode" value={account?.marginMode} />
            {account?.isInvestor && (
              <DetailRow label="Access" value="Investor (read-only)" />
            )}
            <DetailRow
              label="Base digits"
              value={account?.symbolBaseInfo?.digits}
            />
            <DetailRow
              label="Receive user messages"
              value={account?.symbolBaseInfo?.receiveUserMsg}
            />
            <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
              <span className="text-muted-foreground">Trade Flags</span>
              <Badge variant="secondary" className="font-mono">
                {account?.tradeFlags ?? "--"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="px-4 pb-2 pt-4">
            <CardTitle className="text-sm">Broker & Server</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <DetailRow
              label="Company"
              value={account?.companyName || activeAccount?.companyName}
            />
            <DetailRow
              label="Server"
              value={account?.serverName || activeAccount?.serverName}
              copy
            />
            <DetailRow label="Host" value={account?.host || activeAccount?.lastConnectedHost} copy />
            <DetailRow label="Server build" value={account?.serverBuild} />
            <DetailRow label="Type" value={account?.type} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="px-4 pb-2 pt-4">
            <CardTitle className="text-sm">Symbol Base Info</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <DetailRow
              label="Company"
              value={account?.symbolBaseInfo?.companyName}
            />
            <DetailRow
              label="Currency"
              value={account?.symbolBaseInfo?.currency}
            />
            <DetailRow
              label="Account method"
              value={account?.symbolBaseInfo?.accountMethod}
            />
            <DetailRow
              label="Margin mode"
              value={account?.symbolBaseInfo?.marginMode}
            />
            <DetailRow
              label="Symbol set"
              value={formatArray(account?.symbolBaseInfo?.symbolSet)}
            />
            <DetailRow
              label="Symbols hash"
              value={account?.symbolBaseInfo?.symbolsHash}
            />
            <DetailRow
              label="Spreads hash"
              value={account?.symbolBaseInfo?.spreadsHash}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="px-4 pb-2 pt-4">
            <CardTitle className="text-sm">Profile</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <DetailRow label="Name" value={account?.userName} />
            <DetailRow label="Login" value={account?.login || activeAccount?.credentials.login} copy />
            <DetailRow label="Email" value={account?.email} />
            <DetailRow label="Phone" value={account?.phone} />
            <DetailRow label="Country" value={account?.country} />
            <DetailRow label="City" value={account?.city} />
            <DetailRow label="Address" value={account?.userAddress} />
          </CardContent>
        </Card>
      </div>
    </>
  );

  if (embedded) {
    return <div className="flex h-full flex-col bg-background text-foreground">{content}</div>;
  }

  return <div className="flex h-full flex-col bg-background text-foreground">{content}</div>;
}
